import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Baby, User, School, Sun, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AUDIENCES = [
  {
    id: 'kids',
    title: 'Kids',
    subtitle: 'Ages 3-14',
    description: 'Fun, safe lessons for young swimmers',
    icon: Baby,
    filter: 'Kids',
    color: 'bg-cyan-500',
    lightColor: 'bg-cyan-50',
    borderColor: 'border-cyan-200',
  },
  {
    id: 'adults',
    title: 'Adults',
    subtitle: 'Ages 18+',
    description: "It's never too late to learn",
    icon: User,
    filter: 'Adults',
    color: 'bg-primary',
    lightColor: 'bg-primary/5',
    borderColor: 'border-primary/20',
  },
  {
    id: 'schools',
    title: 'Schools',
    subtitle: 'Group Programs',
    description: 'Curriculum-integrated swim education',
    icon: School,
    filter: 'Schools',
    color: 'bg-amber-500',
    lightColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
  },
  {
    id: 'holiday',
    title: 'Holiday',
    subtitle: 'Seasonal Camps',
    description: 'Intensive holiday swim programs',
    icon: Sun,
    filter: 'Holiday',
    color: 'bg-rose-500',
    lightColor: 'bg-rose-50',
    borderColor: 'border-rose-200',
  },
];

const ProgramAudienceSelector = () => {
  const [config, setConfig] = useState(null);
  const [enabled, setEnabled] = useState(true);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/landing/conversion-config`);
      if (res.ok) {
        const data = await res.json();
        setConfig(data);
        setEnabled(data.enable_program_selector !== false);
      }
    } catch (error) {
      console.error('Failed to load conversion config:', error);
    }
  };

  const trackClick = async (audience) => {
    try {
      await fetch(`${BACKEND_URL}/api/public/track-click`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          page: '/',
          cta_key: `audience_selector_${audience.id}`,
          label: audience.title,
          href: `/programs?filter=${audience.filter}`
        })
      });
    } catch (e) {
      // Never block navigation
    }
  };

  if (!enabled) return null;

  return (
    <section className="py-16 bg-white" data-testid="program-audience-selector">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">
            Find Your Program
          </Badge>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-3">
            Who Are You Swimming For?
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Select your profile to find the perfect program
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {AUDIENCES.map((audience) => {
            const IconComponent = audience.icon;
            return (
              <Link
                key={audience.id}
                to={`/programs?filter=${audience.filter}`}
                onClick={() => trackClick(audience)}
                className="group"
              >
                <Card className={`h-full border-2 ${audience.borderColor} hover:shadow-lg hover:scale-[1.02] transition-all duration-300 overflow-hidden`}>
                  <CardContent className="p-5 sm:p-6 flex flex-col items-center text-center">
                    <div className={`w-14 h-14 sm:w-16 sm:h-16 rounded-2xl ${audience.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                      <IconComponent className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-1">
                      {audience.title}
                    </h3>
                    <p className="text-xs sm:text-sm text-slate-500 mb-2">
                      {audience.subtitle}
                    </p>
                    <p className="text-xs sm:text-sm text-slate-600 hidden sm:block">
                      {audience.description}
                    </p>
                    <div className="mt-3 flex items-center text-primary text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                      View Programs
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ProgramAudienceSelector;
